import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { Lock } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { ScoreDial } from '@/components/ScoreDial';
import { SignalBadge } from '@/components/SignalBadge';
import { IndicatorDetailDialog } from '@/components/IndicatorDetailDialog';
import { DataStatus } from '@/components/DataStatus';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useLiquidityData } from '@/hooks/useLiquidityData';
import { useSubscription } from '@/hooks/useSubscription';
import { INDICATORS_BY_ID, GROUP_LABELS, GROUP_FLAGS, type IndicatorGroup } from '@/lib/indicators';
import { formatValue, signed } from '@/lib/format';
import type { DomainScore, IndicatorSnapshot } from '@/lib/types';
import { cn } from '@/lib/utils';

// Free users see only a limited subset per domain.
const FREE_LIMIT = 6;

/** Plain-language read of the overall domain score. */
function domainMeaning(domain: DomainScore): string {
  const supportive = domain.groupContributions.filter((g) => g.points > 0).length;
  const negative = domain.groupContributions.filter((g) => g.points < 0).length;
  const base =
    domain.domain === 'liquidity'
      ? domain.score >= 50
        ? 'Liquidity conditions are net supportive.'
        : domain.score >= 40
          ? 'Liquidity conditions are currently mixed.'
          : 'Liquidity conditions are tightening.'
      : domain.score >= 50
        ? "BCH's environment is improving relative to BTC."
        : domain.score >= 40
          ? 'BCH is developing but not yet confirmed.'
          : 'BCH remains weak relative to BTC.';
  if (negative > 0 && supportive > 0) {
    return `${base} Several indicators are acting as headwinds while others provide modest support.`;
  }
  if (negative > 0) return `${base} Most groups are currently a drag on the score.`;
  return `${base} Most groups are currently supportive.`;
}

/** Which groups could most materially move the score if they turn. */
function domainMovers(domain: DomainScore): string {
  const ranked = [...domain.groupContributions]
    .sort((a, b) => Math.abs(b.points) - Math.abs(a.points))
    .slice(0, 3)
    .map((g) => g.label);
  if (ranked.length === 0) return 'No single group dominates the score right now.';
  return `The score would move most if ${ranked.join(', ')} conditions change direction. Watch these groups first.`;
}

const Indicators = () => {
  useSeoMeta({
    title: 'Signals — BCH Radar',
    description: 'Transparent breakdown of every indicator driving the Global Liquidity and BCH Setup scores.',
  });

  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'bch' ? 'bch' : 'liquidity';
  const { data, isLoading } = useLiquidityData();
  const [selected, setSelected] = useState<IndicatorSnapshot | null>(null);

  return (
    <AppLayout>
      <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Signals</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Every score is fully transparent. Tap any factor to see its value,
        change, source, and why it matters.
      </p>

      <Tabs
        value={tab}
        onValueChange={(v) => setParams({ tab: v })}
        className="mt-5"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="liquidity">Global Liquidity</TabsTrigger>
          <TabsTrigger value="bch">BCH Setup</TabsTrigger>
        </TabsList>

        {isLoading || !data ? (
          <div className="mt-5 space-y-3">
            <Skeleton className="h-40 w-full rounded-xl" />
            <Skeleton className="h-64 w-full rounded-xl" />
          </div>
        ) : (
          <>
            <TabsContent value="liquidity" className="mt-5">
              <DomainBreakdown
                domain={data.snapshot.liquidity}
                trend={data.trend30d.liquidity}
                colorClass="text-primary"
                onSelect={setSelected}
              />
            </TabsContent>
            <TabsContent value="bch" className="mt-5">
              <DomainBreakdown
                domain={data.snapshot.bch}
                trend={data.trend30d.bch}
                colorClass="text-chart-2"
                onSelect={setSelected}
              />
            </TabsContent>
          </>
        )}
      </Tabs>

      <IndicatorDetailDialog
        snapshot={selected}
        open={selected !== null}
        onOpenChange={(v) => !v && setSelected(null)}
      />
    </AppLayout>
  );
};

function DomainBreakdown({
  domain,
  trend,
  colorClass,
  onSelect,
}: {
  domain: DomainScore;
  trend: number;
  colorClass: string;
  onSelect: (s: IndicatorSnapshot) => void;
}) {
  const { isPro } = useSubscription();

  // group indicators
  const groups = new Map<IndicatorGroup, IndicatorSnapshot[]>();
  for (const snap of domain.indicators) {
    const def = INDICATORS_BY_ID[snap.indicatorId];
    const arr = groups.get(def.group) ?? [];
    arr.push(snap);
    groups.set(def.group, arr);
  }

  let shown = 0;

  return (
    <div className="space-y-5">
      {/* Summary */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-5">
            <ScoreDial score={domain.score} colorClass={colorClass} size={120} />
            <div className="flex-1 space-y-2">
              <SignalBadge state={domain.state} />
              <div className="flex items-center gap-3 text-sm">
                <span>
                  <span className="text-muted-foreground">30-day: </span>
                  <span className={cn('font-mono font-semibold', trend >= 0 ? 'text-primary' : 'text-red-400')}>
                    {signed(trend)}
                  </span>
                </span>
                <span>
                  <span className="text-muted-foreground">Confidence: </span>
                  <span className="font-mono font-semibold">
                    {Math.round(domain.confidence * 100)}%
                  </span>
                </span>
              </div>
              {domain.confidence < 1 && (
                <p className="text-xs text-yellow-400">
                  ⚠ Some feeds are missing — confidence reduced.
                </p>
              )}
            </div>
          </div>

          {/* group contributions */}
          <div className="mt-4 space-y-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Major contributors
            </p>
            {domain.groupContributions.map((g) => (
              <div key={g.group} className="flex items-center gap-2 text-sm">
                <span className="w-28 shrink-0 text-muted-foreground">{g.label}</span>
                <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-border">
                  <div
                    className={cn(
                      'absolute top-0 h-full rounded-full',
                      g.points >= 0 ? 'bg-primary' : 'bg-red-500',
                    )}
                    style={{
                      width: `${Math.min(100, Math.abs(g.points) * 4)}%`,
                      left: g.points >= 0 ? '50%' : undefined,
                      right: g.points < 0 ? '50%' : undefined,
                    }}
                  />
                  <div className="absolute left-1/2 top-0 h-full w-px bg-border" />
                </div>
                <span
                  className={cn(
                    'w-10 shrink-0 text-right font-mono font-semibold',
                    g.points >= 0 ? 'text-primary' : 'text-red-400',
                  )}
                >
                  {signed(g.points)}
                </span>
              </div>
            ))}
          </div>

          {/* What does this mean + what would change the signal */}
          <div className="mt-4 space-y-3 border-t border-border/50 pt-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                What does this mean?
              </p>
              <p className="mt-1 text-sm text-foreground/90">
                {domainMeaning(domain)}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                What would change the signal?
              </p>
              <p className="mt-1 text-sm text-foreground/90">
                {domainMovers(domain)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Indicator list grouped */}
      {Array.from(groups.entries()).map(([group, snaps]) => (
        <div key={group}>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            <span>{GROUP_FLAGS[group]}</span> {GROUP_LABELS[group]}
          </h3>
          <div className="space-y-2">
            {snaps.map((snap) => {
              const locked = !isPro && shown >= FREE_LIMIT;
              shown += 1;
              const def = INDICATORS_BY_ID[snap.indicatorId];
              if (locked) {
                return (
                  <div
                    key={snap.indicatorId}
                    className="flex items-center justify-between rounded-xl border border-dashed border-border/60 bg-card/40 p-3 text-sm"
                  >
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <Lock className="h-3.5 w-3.5" /> {def.short}
                    </span>
                    <Badge variant="outline" className="border-primary/40 text-primary text-[10px]">
                      PRO
                    </Badge>
                  </div>
                );
              }
              return (
                <button
                  key={snap.indicatorId}
                  onClick={() => onSelect(snap)}
                  className="flex w-full items-center justify-between rounded-xl border border-border/60 bg-card/60 p-3 text-left transition-colors hover:border-primary/50"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <DataStatus quality={snap.quality} showLabel={false} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{def.short}</p>
                      <p className="font-mono text-xs text-muted-foreground">
                        {formatValue(def, snap.value)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-mono text-xs text-muted-foreground">
                        {snap.subScore}/100
                      </p>
                    </div>
                    <span
                      className={cn(
                        'w-11 rounded-md py-1 text-center font-mono text-xs font-semibold',
                        snap.contribution >= 0
                          ? 'bg-primary/15 text-primary'
                          : 'bg-red-500/15 text-red-400',
                      )}
                    >
                      {signed(snap.contribution)}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default Indicators;
